import { User, Organization } from "@/shared/client";

/**
 * Get organizations where user has admin or owner role
 */
export const getUserOrganizationsFromUser = (
  userData: User | undefined
): Organization[] => {
  if (!userData || !userData.roles) return [];

  return userData.roles
    .filter(
      (role) =>
        role.scope === "organization" &&
        role.organization &&
        (role.role === "ORGANIZATION_ADMIN" ||
          role.role === "ORGANIZATION_OWNER")
    )
    .map((role) => role.organization!)
    .filter(
      (org, index, self) =>
        // Remove duplicates by organization ID
        index === self.findIndex((o) => o.id === org.id)
    );
};

/**
 * Check if user can access application
 * Returns true if user has system role or admin/owner org role
 */
export const hasCorrectRole = (userData: User | undefined): boolean => {
  if (!userData || !userData.roles) return false;

  // Check for system role
  const hasSystemRole = userData.roles.some(
    (role) => role.scope === "system"
  );
  if (hasSystemRole) return true;

  // Check for organization role with admin or owner level
  return userData.roles.some(
    (role) =>
      role.scope === "organization" &&
      (role.role === "ORGANIZATION_ADMIN" ||
        role.role === "ORGANIZATION_OWNER")
  );
};

/**
 * Check if user has staff role in any organization
 */
export const isUserOrganizationStaff = (userData: User | undefined): boolean => {
  if (!userData || !userData.roles) return false;
  return userData.roles.some(
    (role) =>
      role.scope === "organization" && role.role === "ORGANIZATION_STAFF"
  );
};

/**
 * Get the appropriate redirect path after authentication
 * Returns the path where the user should be redirected based on their roles.
 * Admin users go to /admin/dashboard so they always see the start page, not just the sidebar.
 */
export const getAuthRedirectPath = (userData: User | undefined): string => {
  if (!userData) return "/login";

  if (hasCorrectRole(userData)) {
    return "/admin/dashboard";
  } else if (isUserOrganizationStaff(userData)) {
    return "/staff-access";
  } else {
    return "/401";
  }
};

/**
 * Extract admins from an organization object
 */
export const getAdminsFromOrganization = (
  organization: Organization | undefined
): User[] => {
  if (!organization || !organization.roles) return [];

  return organization.roles
    .filter(
      (role) =>
        role.scope === "organization" &&
        (role.role === "ORGANIZATION_ADMIN" ||
          role.role === "ORGANIZATION_OWNER")
    )
    .map((role) => role.user)
    .filter((user): user is User => user !== undefined);
};
